/**
 * GatilhosTenantPage — Tela 8 Admin "Gatilhos do Tenant" (`/admin/ia/gatilhos`).
 *
 * Materializa o lado Admin da dupla soberania ADR-148 (Eixo I da ADR-030):
 *   - **Piso de plataforma** (5): sempre-on, travado — exibido read-only com
 *     selo "obrigatório" (ninguém desliga; backend recusa com 400).
 *   - **Mínimos do tenant** (8 configuráveis): marcar um gatilho aqui o força
 *     ON em TODOS os playbooks (a aba 4 do Gestor o mostra travado).
 *   - **Detector-pendente** (3): configurável como intenção de política, porém
 *     INERTE — selo honesto "sem efeito ainda — detector pendente" (ADR-148 §2).
 *
 * Fonte única: catálogo canônico `@lume/shared` (labels/descrições/grupos
 * derivam de `PROTECTION_TRIGGER_CATALOG` — a fixture do mockup não é contrato).
 * Persistência: `PATCH /v1/tenant-config { protectionTriggerMinimums }`
 * (nenhum marcado ⇒ `null` = só o piso). Molde visual: BehavioralTab v3.253.
 *
 * Seção ConflictAlert do mockup = honest-defer: não existe config por
 * alertType no backend; tabela de toggles seria affordance falsa.
 *
 * Gates: leitura `tenant-config-read` (a QUERY é gateada, não só o render);
 * escrita `tenant-config-manage`.
 */

import {
  PROTECTION_TRIGGER_CATALOG,
  type ProtectionTriggerDefinition,
  type ProtectionTriggerId,
} from '@lume/shared';
import { useEffect, useMemo, useState } from 'react';

import { useCapability } from '../../../hooks/use-capability';
import { FeatureLocked } from '../../shared/FeatureLocked';
import { useTenantConfig, useUpdateTenantConfig } from '../config-tenant/use-tenant-config';

import type { SerializedTenantConfig } from '../config-tenant/types';

const PLATFORM_FLOOR: readonly ProtectionTriggerDefinition[] = PROTECTION_TRIGGER_CATALOG.filter(
  (t) => t.platformFloor,
);
const CONFIGURABLE_HARD: readonly ProtectionTriggerDefinition[] = PROTECTION_TRIGGER_CATALOG.filter(
  (t) => !t.platformFloor && t.kind === 'dura',
);
const TRANSITION: readonly ProtectionTriggerDefinition[] = PROTECTION_TRIGGER_CATALOG.filter(
  (t) => t.kind === 'virada',
);
const CONFIGURABLE: readonly ProtectionTriggerDefinition[] = [...CONFIGURABLE_HARD, ...TRANSITION];

type MinimumsMap = Record<string, boolean>;

function toForm(config: SerializedTenantConfig): MinimumsMap {
  const minimums: readonly string[] = config.protectionTriggerMinimums ?? [];
  const map: MinimumsMap = {};
  for (const def of CONFIGURABLE) {
    map[def.id] = minimums.includes(def.id);
  }
  return map;
}

/** Nenhum marcado ⇒ `null` (semântica canônica "só o piso de plataforma"). */
function buildPayload(form: MinimumsMap): ProtectionTriggerId[] | null {
  const selected = CONFIGURABLE.filter((def) => form[def.id] === true).map(
    (def) => def.id as ProtectionTriggerId,
  );
  return selected.length === 0 ? null : selected;
}

export function GatilhosTenantPage(): JSX.Element {
  const canRead = useCapability('tenant-config-read');
  const canManage = useCapability('tenant-config-manage');
  // ADR-148/Item 350 FE — gateia a QUERY (não só o render): sem a cap, o GET
  // nem dispara (evita 403 vazando em toast).
  const query = useTenantConfig(canRead);
  const update = useUpdateTenantConfig();
  const [form, setForm] = useState<MinimumsMap | null>(null);

  useEffect(() => {
    if (query.data !== undefined) setForm(toForm(query.data));
  }, [query.data]);

  const serverForm = useMemo(
    () => (query.data !== undefined ? toForm(query.data) : null),
    [query.data],
  );

  const isDirty = useMemo(() => {
    if (form === null || serverForm === null) return false;
    return JSON.stringify(form) !== JSON.stringify(serverForm);
  }, [form, serverForm]);

  if (!canRead) {
    return <FeatureLocked capability="tenant-config-read" />;
  }

  if (query.isLoading) {
    return (
      <div className="px-8 py-6">
        <p className="text-sm text-text-secondary">Carregando gatilhos…</p>
      </div>
    );
  }
  if (query.isError || query.data === undefined || form === null) {
    return (
      <div className="px-8 py-6">
        <p className="text-sm text-red-600">Falha ao carregar a configuração de gatilhos.</p>
      </div>
    );
  }

  const fieldsDisabled = !canManage || update.isPending;

  const setMinimum = (id: string, value: boolean): void => {
    setForm((f) => (f === null ? f : { ...f, [id]: value }));
  };

  const handleSave = (): void => {
    if (!isDirty) return;
    update.mutate({ protectionTriggerMinimums: buildPayload(form) });
  };

  const handleDiscard = (): void => {
    if (serverForm !== null) setForm(serverForm);
    update.reset();
  };

  return (
    <div className="flex h-full flex-col">
      <header className="border-b border-border-subtle bg-surface px-8 py-5">
        <p className="text-xs uppercase tracking-wide text-text-secondary">
          Configuração · Tenant cross-playbook
        </p>
        <h1 className="text-xl font-semibold text-text-primary">Gatilhos de Proteção</h1>
        <p className="mt-0.5 text-sm text-text-secondary">
          Proteções do modo IA-conduz (H_c): piso de plataforma + mínimos do tenant que valem para
          todos os playbooks.
        </p>
      </header>

      <div className="flex-1 overflow-y-auto px-8 py-6">
        <div className="max-w-2xl space-y-6">
          <div className="rounded-xl border border-border-subtle bg-app-bg px-4 py-3 text-sm text-text-secondary">
            Gatilhos marcados aqui ficam <strong className="text-text-primary">travados</strong> em
            todos os playbooks — o Gestor não consegue desligá-los na aba Gatilhos do playbook.
          </div>

          {/* Piso de plataforma — sempre-on, ninguém desliga */}
          <section className="rounded-xl border border-red-200 bg-surface p-4">
            <h2 className="text-sm font-semibold text-red-700">
              Piso de plataforma ({PLATFORM_FLOOR.length.toString()} — não-negociáveis)
            </h2>
            <p className="mt-0.5 mb-3 text-xs text-red-700/80">
              Essência ética da plataforma (ADR-030): sempre ativos, em qualquer playbook e qualquer
              plano. Ninguém desliga.
            </p>
            <div className="space-y-2">
              {PLATFORM_FLOOR.map((def) => (
                <label
                  key={def.id}
                  className="flex items-start gap-2 rounded-lg border border-border-subtle px-3 py-2 text-sm text-text-secondary"
                >
                  <input
                    type="checkbox"
                    checked
                    disabled
                    aria-label={def.label}
                    className="mt-0.5 h-4 w-4 rounded border-border-subtle text-accent opacity-60"
                  />
                  <span className="flex-1">
                    <span className="flex items-center gap-2 font-medium text-text-primary">
                      {def.label}
                      <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-red-700">
                        obrigatório
                      </span>
                    </span>
                    <span className="mt-0.5 block text-xs">{def.description}</span>
                  </span>
                </label>
              ))}
            </div>
          </section>

          {/* Escalações duras configuráveis — mínimos do tenant */}
          <section className="rounded-xl border border-border-subtle bg-surface p-4">
            <h2 className="text-sm font-semibold text-text-primary">
              Escalações duras configuráveis ({CONFIGURABLE_HARD.length.toString()})
            </h2>
            <p className="mt-0.5 mb-3 text-xs text-text-secondary">
              Marque para tornar mínimo do tenant: o gatilho fica forçado em todos os playbooks.
              Desmarcado, cada playbook decide (aba Gatilhos do Gestor).
            </p>
            <div className="space-y-2">
              {CONFIGURABLE_HARD.map((def) => (
                <TriggerRow
                  key={def.id}
                  def={def}
                  checked={form[def.id] === true}
                  disabled={fieldsDisabled}
                  onChange={(v) => {
                    setMinimum(def.id, v);
                  }}
                />
              ))}
            </div>
          </section>

          {/* Gatilhos de virada */}
          <section className="rounded-xl border border-border-subtle bg-surface p-4">
            <h2 className="text-sm font-semibold text-text-primary">
              Gatilhos de virada de playbook ({TRANSITION.length.toString()})
            </h2>
            <p className="mt-0.5 mb-3 text-xs text-text-secondary">
              Disparam avaliação de troca de playbook durante o H_c (matriz ADR-031). Marcados como
              mínimo, nenhum playbook consegue desligá-los.
            </p>
            <div className="space-y-2">
              {TRANSITION.map((def) => (
                <TriggerRow
                  key={def.id}
                  def={def}
                  checked={form[def.id] === true}
                  disabled={fieldsDisabled}
                  onChange={(v) => {
                    setMinimum(def.id, v);
                  }}
                />
              ))}
            </div>
          </section>

          {/* ConflictAlert — honest-defer (sem config por alertType no backend) */}
          <section className="rounded-xl border border-dashed border-border-subtle bg-app-bg px-4 py-3">
            <h2 className="text-sm font-medium text-text-primary">
              ConflictAlert por tipo (ADR-117)
            </h2>
            <p className="mt-0.5 text-xs text-text-secondary">
              A configuração de severidade/ativação por alertType ainda não está disponível. Alertas
              de conflito são gerenciados na Central de Alertas do Supervisor.
            </p>
          </section>

          {/* Footer */}
          {canManage ? (
            <div className="flex items-center justify-end gap-3">
              {update.isError && (
                <span role="alert" className="mr-auto text-sm text-red-600">
                  Falha ao salvar. Tente novamente.
                </span>
              )}
              {update.isSuccess && !isDirty && (
                <span className="mr-auto text-sm text-emerald-600">Alterações salvas.</span>
              )}
              <button
                type="button"
                onClick={handleDiscard}
                disabled={!isDirty || update.isPending}
                className="rounded border border-border-subtle px-4 py-2 text-sm text-text-primary hover:bg-app-bg disabled:opacity-50"
              >
                Descartar
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!isDirty || update.isPending}
                className="rounded bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
              >
                {update.isPending ? 'Salvando…' : 'Salvar alterações'}
              </button>
            </div>
          ) : (
            <p className="rounded-lg border border-border-subtle bg-app-bg px-4 py-3 text-sm text-text-secondary">
              Você tem acesso de leitura. A edição dos mínimos do tenant é restrita ao administrador
              do tenant.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function TriggerRow({
  def,
  checked,
  disabled,
  onChange,
}: {
  readonly def: ProtectionTriggerDefinition;
  readonly checked: boolean;
  readonly disabled: boolean;
  readonly onChange: (value: boolean) => void;
}): JSX.Element {
  const pending = def.signal === 'pending';
  return (
    <label
      className={`flex items-start gap-2 rounded-lg border border-border-subtle px-3 py-2 text-sm ${
        disabled
          ? 'cursor-not-allowed text-text-secondary'
          : 'cursor-pointer text-text-primary hover:bg-app-bg'
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-label={def.label}
        onChange={(e) => {
          onChange(e.target.checked);
        }}
        className="mt-0.5 h-4 w-4 rounded border-border-subtle text-accent focus:ring-accent disabled:opacity-50"
      />
      <span className="flex-1">
        <span className="flex flex-wrap items-center gap-2 font-medium text-text-primary">
          {def.label}
          {pending && (
            <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700">
              sem efeito ainda — detector pendente
            </span>
          )}
        </span>
        <span className="mt-0.5 block text-xs text-text-secondary">{def.description}</span>
      </span>
    </label>
  );
}
